import { type FC } from "react";
import {
  Chip,
  CircularProgress,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import {
  ComplianceRecordKindType,
  type ComplianceRecordsQueryType,
} from "@/types/graphql/partner.generated";

export type ComplianceRecord =
  ComplianceRecordsQueryType["complianceRecords"][number];

interface ComplianceRecordsListProps {
  records: ComplianceRecord[];
  loading: boolean;
  onEdit: (record: ComplianceRecord) => void;
  onDelete: (record: ComplianceRecord) => void;
}

export const ComplianceRecordsList: FC<ComplianceRecordsListProps> = ({
  records,
  loading,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return <CircularProgress />;
  }

  if (records.length === 0) {
    return (
      <Typography color="textSecondary" variant="body2">
        {t("compliance.empty")}
      </Typography>
    );
  }

  return (
    <List>
      {records.map((record) => (
        <ListItem
          key={record.id}
          secondaryAction={
            <Stack direction="row" spacing={1}>
              <IconButton onClick={() => onEdit(record)} size="small">
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={() => onDelete(record)} size="small">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
        >
          <ListItemText
            disableTypography
            primary={
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  label={t(`compliance.kind.${record.kind}`)}
                />
                <Typography variant="body1" component="span">
                  {record.performerName || record.fileName || record.url}
                </Typography>
              </Stack>
            }
            secondary={
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {record.note && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    component="span"
                  >
                    {record.note}
                  </Typography>
                )}
                {record.kind === ComplianceRecordKindType.FileType &&
                  record.fileUrl && (
                    // Ссылка подписана бэкендом на 15 минут: открываем в
                    // новой вкладке, в кабинете её не кешируем.
                    <Link
                      href={record.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                    >
                      {t("compliance.download")}
                    </Link>
                  )}
                {record.kind === ComplianceRecordKindType.LinkType &&
                  record.url && (
                    <Link
                      href={record.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {record.url}
                    </Link>
                  )}
              </Stack>
            }
          />
        </ListItem>
      ))}
    </List>
  );
};
